/**
 * 会员管理API
 */
import request from '@/utils/request'

/**
 * 获取会员等级列表（用户端）
 */
export function getMembershipTiers() {
  return request({
    url: '/api/v1/membership/tiers',
    method: 'get'
  })
}

/**
 * 获取会员等级详情
 */
export function getMembershipTierDetail(id) {
  return request({
    url: `/api/v1/membership/tiers/${id}`,
    method: 'get'
  })
}

/**
 * 获取当前用户会员信息
 */
export function getCurrentMembership() {
  return request({
    url: '/api/v1/membership/current',
    method: 'get'
  })
}

/** 当前用户会员档位允许的 AI 模型列表 */
export function getMyAvailableModels() {
  return request({
    url: '/api/v1/membership/my-available-models',
    method: 'get',
  })
}

/**
 * 购买会员套餐
 */
export function purchaseMembership(data) {
  return request({
    url: '/api/v1/membership/purchase',
    method: 'post',
    data
  })
}

/**
 * 计算会员升级价格（旧版API，保留兼容）
 * @param {Object} data - { target_tier_code: string, subscription_type: 'monthly'|'yearly' }
 * @returns {Promise} - { original_price, remaining_value, discount_amount, actual_price, remaining_days, ... }
 */
export function calculateUpgradePrice(data) {
  return request({
    url: '/api/v1/membership/calculate-upgrade-price',
    method: 'post',
    data
  })
}

/**
 * 计算会员升级选项（v6.1新版API - 返回三种方案）
 * @param {Object} data - { target_tier_code: string, target_subscription_type: 'monthly'|'yearly' }
 * @returns {Promise} - { success, options: [...], recommended_option_id, total_options, message, ... }
 */
export function calculateUpgradeOptions(data) {
  return request({
    url: '/api/v1/membership/upgrade-options/calculate-options',
    method: 'post',
    data
  })
}

/**
 * 预览升级选项（简化版）
 * @param {string} targetTierCode - 目标等级代码 (plus/pro/enterprise)
 * @param {string} subscriptionType - 订阅类型 (monthly/yearly)
 * @returns {Promise} - 升级选项列表
 */
export function previewUpgradeOptions(targetTierCode, subscriptionType = 'monthly') {
  return request({
    url: `/api/v1/membership/upgrade-options/preview/${targetTierCode}`,
    method: 'get',
    params: { subscription_type: subscriptionType }
  })
}

/**
 * 执行升级（选择指定方案）
 * @param {Object} data - { 
 *   target_tier_code: string, 
 *   target_subscription_type: string,
 *   option_id: number,  // 1=立即升级, 2=保留时长, 3=到期升级
 *   payment_channel: string,
 *   client_ip: string
 * }
 * @returns {Promise} - 支付订单信息
 */
export function executeUpgradeWithOption(data) {
  return request({
    url: '/api/v1/membership/upgrade-options/execute',
    method: 'post',
    data
  })
}

/**
 * 取消会员订阅
 */
export function cancelMembership(membershipId) {
  return request({
    url: '/api/v1/membership/cancel',
    method: 'post',
    data: { membership_id: membershipId }
  })
}

/**
 * 获取会员订阅历史
 */
export function getMembershipHistory(params) {
  return request({
    url: '/api/v1/membership/history',
    method: 'get',
    params
  })
}

// ==================== X-Coin管理API ====================

/**
 * 获取X-Coin余额汇总
 */
export function getXCoinBalanceSummary() {
  return request({
    url: '/api/v1/xcoin/balance/summary',
    method: 'get'
  })
}

/**
 * 获取X-Coin余额明细
 */
export function getXCoinBalanceDetails(params) {
  return request({
    url: '/api/v1/xcoin/balance/details',
    method: 'get',
    params
  })
}

/**
 * 检查X-Coin余额是否充足
 */
export function checkXCoinBalance(data) {
  return request({
    url: '/api/v1/xcoin/balance/check',
    method: 'post',
    data
  })
}

/**
 * 获取X-Coin交易流水
 */
export function getXCoinTransactions(params) {
  return request({
    url: '/api/v1/xcoin/transactions',
    method: 'get',
    params
  })
}

/**
 * 获取X-Coin交易统计
 */
export function getXCoinTransactionSummary(params) {
  return request({
    url: '/api/v1/xcoin/transactions/summary',
    method: 'get',
    params
  })
}

// ==================== 邀请奖励API ====================

/**
 * 获取邀请统计
 */
export function getInvitationStats() {
  return request({
    url: '/api/v1/invitation/stats',
    method: 'get'
  })
}

/**
 * 获取邀请记录列表
 */
export function getInvitationList(params) {
  return request({
    url: '/api/v1/invitation/list',
    method: 'get',
    params
  })
}

/**
 * 获取邀请奖励记录
 */
export function getInvitationRewards(params) {
  return request({
    url: '/api/v1/invitation/rewards',
    method: 'get',
    params
  })
}

/**
 * 获取我的邀请码
 */
export function getMyInvitationCode() {
  return request({
    url: '/api/v1/invitation/invitation-code',
    method: 'get'
  })
}

// ==================== 后台会员管理API（管理员） ====================

/**
 * 获取所有会员等级配置（管理员）
 */
export function adminGetAllMembershipTiers(params) {
  return request({
    url: '/api/v1/admin/membership/tiers',
    method: 'get',
    params
  })
}

/**
 * 获取会员等级详情（管理员）
 */
export function adminGetMembershipTierDetail(id) {
  return request({
    url: `/api/v1/admin/membership/tiers/${id}`,
    method: 'get'
  })
}

/**
 * 创建会员等级（管理员）
 */
export function adminCreateMembershipTier(data) {
  return request({
    url: '/api/v1/admin/membership/tiers',
    method: 'post',
    data
  })
}

/**
 * 更新会员等级（管理员）
 */
export function adminUpdateMembershipTier(id, data) {
  return request({
    url: `/api/v1/admin/membership/tiers/${id}`,
    method: 'put',
    data
  })
}

/**
 * 删除会员等级（管理员）
 */
export function adminDeleteMembershipTier(id) {
  return request({
    url: `/api/v1/admin/membership/tiers/${id}`,
    method: 'delete'
  })
}

/**
 * 切换会员等级激活状态（管理员）
 */
export function adminToggleTierActive(id) {
  return request({
    url: `/api/v1/admin/membership/tiers/${id}/toggle-active`,
    method: 'post'
  })
}

/**
 * 切换会员等级可见状态（管理员）
 */
export function adminToggleTierVisible(id) {
  return request({
    url: `/api/v1/admin/membership/tiers/${id}/toggle-visible`,
    method: 'post'
  })
}

/**
 * 获取会员订阅统计（管理员）
 */
export function adminGetMembershipStatistics() {
  return request({
    url: '/api/v1/admin/membership/memberships/statistics',
    method: 'get'
  })
}

/** 全站报告并发占用概览（管理后台） */
export function adminGetReportConcurrencyOverview() {
  return request({
    url: '/api/v1/admin/membership/report-concurrency/overview',
    method: 'get'
  })
}

/**
 * 获取所有用户会员列表（管理员）
 */
export function adminGetMembersList(params) {
  return request({
    url: '/api/v1/admin/membership/members/list',
    method: 'get',
    params
  })
}

/**
 * 快速升级用户会员（管理员）
 */
export function adminUpgradeMembership(userId, data) {
  return request({
    url: `/api/v1/admin/membership/users/${userId}/upgrade`,
    method: 'post',
    data
  })
}

/**
 * 降级用户会员（管理员）
 */
export function adminDowngradeMembership(userId, data) {
  return request({
    url: `/api/v1/admin/membership/users/${userId}/downgrade`,
    method: 'post',
    data
  })
}

/**
 * 为用户分配会员等级（管理员）
 */
export function adminAssignMembership(userId, data) {
  return request({
    url: `/api/v1/admin/membership/users/${userId}/memberships`,
    method: 'post',
    data
  })
}

/**
 * 获取用户会员列表（管理员）
 */
export function adminGetUserMemberships(userId, params) {
  return request({
    url: `/api/v1/admin/membership/users/${userId}/memberships`,
    method: 'get',
    params
  })
}

/**
 * 获取用户会员详情（管理员）
 */
export function adminGetUserMembershipDetail(userId, membershipId) {
  return request({
    url: `/api/v1/admin/membership/users/${userId}/memberships/${membershipId}`,
    method: 'get'
  })
}

/**
 * 获取可用于会员配置的AI模型列表（管理员）
 */
export function adminGetAvailableModels(params) {
  return request({
    url: '/api/v1/admin/membership/available-models',
    method: 'get',
    params
  })
}

